import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Sparkles, Zap, Flame, Clock, ArrowLeft, Play, 
  CheckCircle, Trophy, Calendar
} from 'lucide-react';

const DailyPractice = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPractice();
  }, []);

  const fetchPractice = async () => {
    try {
      const response = await api.get('/daily');
      setPractice(response.data);
    } catch (error) {
      console.error('Error fetching daily practice:', error);
      toast.error('Failed to load daily practice');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      await api.post('/daily/complete', { time_spent_minutes: 5 });
      toast.success('Daily practice recorded!');
      fetchPractice();
    } catch (error) {
      console.error('Error completing practice:', error);
      toast.error('Failed to record practice');
    }
  };

  const dailyExercises = [
    { type: 'Vocabulary', description: 'Learn 5 new words', time: '2 min' },
    { type: 'Listening', description: 'Short audio clip', time: '2 min' },
    { type: 'Grammar', description: 'Quick grammar drill', time: '1 min' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFCF8]/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="font-bold">Daily Practice</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Streak Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-orange-500 to-red-500 rounded-3xl p-8 mb-8 text-white"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center">
                <Flame className="w-10 h-10" />
              </div>
              <div>
                <div className="text-5xl font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {practice?.streak_count || 0}
                </div>
                <p className="text-white/80">Day Streak</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5" />
                <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
              </div>
              <p className="text-white/80">{practice?.exercises_completed || 0} exercises today</p>
            </div>
          </div>
        </motion.div>

        {/* Today's Exercises */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
            Today's 5-Minute Practice
          </h2>

          <div className="space-y-4 mb-8">
            {dailyExercises.map((exercise, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * (index + 1) }}
                className="bg-white rounded-2xl p-6 border border-border/50 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    {practice?.exercises_completed > index ? (
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    ) : (
                      <Play className="w-6 h-6 text-primary" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold">{exercise.type}</h3>
                    <p className="text-sm text-muted-foreground">{exercise.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {exercise.time}
                  </span>
                  {practice?.exercises_completed > index ? (
                    <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
                      Done
                    </span>
                  ) : (
                    <Button size="sm" className="rounded-full">
                      Start
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Complete Button */}
          {practice?.exercises_completed < 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <Button
                size="lg"
                className="rounded-full px-10 py-6 font-bold"
                onClick={handleComplete}
              >
                <Zap className="w-5 h-5 mr-2" />
                Complete Today's Practice
              </Button>
              <p className="mt-4 text-sm text-muted-foreground">
                Complete all exercises to maintain your streak!
              </p>
            </motion.div>
          )}

          {/* Completed State */}
          {practice?.exercises_completed >= 3 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-50 rounded-2xl p-8 text-center border border-green-200"
            >
              <Trophy className="w-16 h-16 mx-auto mb-4 text-green-600" />
              <h3 className="text-xl font-bold mb-2 text-green-800">Today's Practice Complete!</h3>
              <p className="text-green-600 mb-4">
                Great job! Come back tomorrow to continue your streak.
              </p>
              <Button variant="outline" className="rounded-full" onClick={() => navigate(-1)}>
                Back to Dashboard
              </Button>
            </motion.div>
          )}
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 grid md:grid-cols-3 gap-4"
        >
          <div className="bg-white rounded-xl p-6 border border-border/50 text-center">
            <div className="text-3xl font-bold text-primary mb-1">{practice?.streak_count || 0}</div>
            <div className="text-sm text-muted-foreground">Current Streak</div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-border/50 text-center">
            <div className="text-3xl font-bold text-primary mb-1">{practice?.exercises_completed || 0}</div>
            <div className="text-sm text-muted-foreground">Today's Exercises</div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-border/50 text-center">
            <div className="text-3xl font-bold text-primary mb-1">{practice?.time_spent_minutes || 0}</div>
            <div className="text-sm text-muted-foreground">Minutes Today</div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default DailyPractice;
